import { DataTable } from "@/components/DataTable/dataTable.component";
import QuotePDFActions from "@/components/QuotePDFActions/quotePDFActions.component";
import QuoteAcceptDialog from "@/components/QuoteTrackingTable/quoteAcceptDialog";
import QuoteRejectDialog from "@/components/QuoteTrackingTable/quoteRejectDialog";
import ViewPDF from "@/components/ViewPDF/viewPDF.component";
import { fetchSingleQuote } from "@/models/socket/quotes.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import { Card, CardContent } from "@/shadcn/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { ConvertedQuoteItem, FullSingleQuote } from "@/types";
import { convertQuoteItem } from "@/utils/dataConversion";
import {
  exportSingleQuoteToCSVs,
  exportSingleQuoteToExcel,
} from "@/utils/Exports/exportSingleQuote.util";
import { genericFailureToast } from "@/utils/utils.util";
import { ColumnDef } from "@tanstack/react-table";
import { Clock, ClockAlert, SquareCheck, SquareX, X } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";

type SingleQuoteTableProps = {
  quoteID: string;
  closeTable: () => void;
  refetchQuotesData: () => void;
};

const SingleQuoteTable: React.FC<SingleQuoteTableProps> = ({
  quoteID,
  closeTable,
  refetchQuotesData,
}) => {
  const [singleQuote, setSingleQuote] = useState<FullSingleQuote | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [convertedItems, setConvertedItems] = useState<ConvertedQuoteItem[]>(
    []
  );
  const { userRole } = useContext(AppContext);
  const [viewingPDF, setViewingPDF] = useState<boolean>(false);

  useEffect(() => {
    async function fetchSingleData() {
      if (!quoteID) return;

      setLoading(true);
      setError(null);
      try {
        const response = await fetchSingleQuote(quoteID);
        if (response.rStatus === "error") {
          throw new Error(response.rData);
        }
        setSingleQuote(response.rData);
        const covetedItemsList = await convertQuoteItem(response.rData);
        setConvertedItems(covetedItemsList);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    }

    fetchSingleData();
  }, [quoteID]);

  function downloadFileFromBase64(
    base64String: string | null | undefined,
    fileName: string
  ) {
    if (!base64String) {
      console.error("No file data available to download.");
      return;
    }
    console.log("Downloading file:", fileName);
    console.log("Base64 String:", base64String);
    const link = document.createElement("a");
    link.href = base64String;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function updateQuoteStatus(newStatus: FullSingleQuote["quote_status"]) {
    if (!singleQuote) return;
    setSingleQuote((prev) => {
      if (!prev) return null;
      return { ...prev, quote_status: newStatus };
    });
    refetchQuotesData();
  }

  function exportToExcel() {
    if (!singleQuote) {
      console.error("No quote data available for export");
      return;
    }
    exportSingleQuoteToExcel(singleQuote);
  }

  function exportToCSV() {
    if (!singleQuote) {
      console.error("No quote data available for export");
      return;
    }
    exportSingleQuoteToCSVs(singleQuote);
  }

  function sendQueryEmail() {
    // Use the assigned Vulcan contact for direct communication
    const queryEmail = singleQuote?.quoted_by_email;
    if (!queryEmail) {
      console.error("No email available to send query");
      genericFailureToast(
        "No email address found for the quote. Please contact support."
      );
      return;
    }
    // Construct subject with quote number for clear reference
    const subject = singleQuote?.quote_number
      ? `Query regarding Quote #${singleQuote.quote_number}`
      : "Quote Query";
    // Use mailto for seamless user experience with default email client
    window.location.href = `mailto:${encodeURIComponent(
      queryEmail
    )}?subject=${encodeURIComponent(subject)}`;
  }

  const columns: ColumnDef<ConvertedQuoteItem>[] = [
    // {
    //   header: "View",
    //   accessorKey: "view",
    //   cell: ({ row }) => {
    //     // Add view action at the end for user convenience
    //     return (
    //       <div className="flex items-center justify-center">
    //         <DialogViewImage imgScr={row.original.item_file_meta || ""} />
    //       </div>
    //     );
    //   },
    //   enableSorting: false,
    //   enableHiding: false,
    // },
    {
      header: "Line Item Number",
      accessorKey: "line_no",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      // Include line number for item identification and reference
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Drawing Number",
      accessorKey: "item_file_drawing_no",
      enableSorting: true,
      enableHiding: false,
      size: 250,
      // Include drawing number for engineering traceability
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Part Name",
      accessorKey: "name",
      enableSorting: true,
      enableHiding: false,
      size: 200,
      // Include part name for user clarity on item identity
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Item Description",
      accessorKey: "description",
      enableSorting: true,
      enableHiding: false,
      size: 300,
      // Include description for detailed item context
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Material Type",
      accessorKey: "material_type",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      // Include material type for manufacturing and quoting accuracy
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Material Thickness",
      accessorKey: "thickness",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      // Include thickness for technical and pricing reasons
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Material Width",
      accessorKey: "width",
      enableSorting: true,
      enableHiding: false,
      size: 120,
      // Include width for technical and pricing reasons
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Material Length",
      accessorKey: "length",
      enableSorting: true,
      enableHiding: false,
      size: 120,
      // Include length for technical and pricing reasons
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Quantity Ordered",
      accessorKey: "qty",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      // Include quantity for order fulfillment and pricing
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Price per Item",
      accessorKey: "price_per_item",
      enableSorting: true,
      enableHiding: false,
      size: 110,
      // Include unit price for financial transparency
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Total Price",
      accessorKey: "total_price_per_item",
      enableSorting: true,
      enableHiding: false,
      size: 90,
      // Include total price for financial transparency
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Process Type",
      accessorKey: "process_type",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      // Include process type (e.g., Cut or Bent) for manufacturing clarity
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Bending Tolerance",
      accessorKey: "bending_tolerance",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      // Include bending tolerance for quality and specification compliance
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
  ];

  return (
    <Card className="relative bg-black/10">
      <X
        className="absolute top-2 right-2 cursor-pointer text-white"
        onClick={closeTable}
      />
      {loading ? (
        <div className="py-4">
          <div className="flex items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
          </div>
          <div className="flex items-center justify-center">
            <h2 className="mt-2 text-lg text-white">
              Loading Quote Details...
            </h2>
          </div>
        </div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : !singleQuote ? (
        <div className="flex items-center justify-center">
          <p className="hidden text-red-500">
            {/* Should not happen, handled by error */}
            No order details found for ID: {quoteID}
          </p>
        </div>
      ) : (
        <>
          {/* <CardHeader className="m-0 p-0">
            <CardTitle className="text-center text-lg font-bold">
              <div>Quote Details for quote {quoteID}</div>
            </CardTitle>
          </CardHeader> */}
          {/* {singleQuote.file_meta && (
            <Button
              className="m-auto w-auto"
              onClick={() =>
                downloadFileFromBase64(
                  singleQuote.file_meta,
                  `quote_${singleQuote.quote_number}`
                )
              }
            >
              <Download className="mr-2 h-4 w-4" />
              Download Quote
            </Button>
          )} */}
          {viewingPDF && singleQuote.file_meta && (
            <ViewPDF
              fileBase64={singleQuote.file_meta}
              closeViewer={() => setViewingPDF(false)}
            />
          )}
          <CardContent>
            <div className="flex flex-wrap justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  className="mb-4"
                  // variant="outline"
                  onClick={exportToExcel}
                >
                  Export to Excel
                </Button>
                <Button
                  className="mb-4"
                  // variant="outline"
                  onClick={exportToCSV}
                >
                  Export to CSV
                </Button>
                {/* {singleQuote.file_meta && (
                  <Button
                    className="mb-4"
                    // variant="outline"
                    onClick={() => setViewingPDF(true)}
                  >
                    View PDF
                  </Button>
                )} */}
                <QuotePDFActions quote={singleQuote} />
              </div>
              <div className="mt-2 mr-2 flex justify-end gap-4">
                {singleQuote.quote_status === "accepted" ? (
                  <TooltipProvider>
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger>
                        <div className="text-quote-accepted flex h-min items-center gap-2 rounded-md bg-neutral-800/20 p-2 py-1.5 outline-[1.5px] outline-neutral-800 select-none">
                          <SquareCheck className="h-5 w-5" />
                          Quote Accepted
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          You've approved the quote. We'll proceed with the next
                          steps.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : singleQuote.quote_status === "rejected" ? (
                  <TooltipProvider>
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger>
                        <div className="text-quote-rejected flex h-min items-center gap-2 rounded-md bg-neutral-800/20 p-2 py-1.5 outline-[1.5px] outline-neutral-800 select-none">
                          <SquareX className="h-5 w-5" />
                          Quote Rejected
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          You've declined the quote. No further action will be
                          taken.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : singleQuote.quote_status === "open" ? (
                  // <div className="text-primary flex h-min items-center gap-2 rounded-md bg-neutral-800/20 p-2 py-1.5 outline-[1.5px] outline-neutral-800 select-none">
                  //   <Clock className="h-5 w-5" />
                  //   Quote Open
                  // </div>
                  userRole === "approved_user" ? (
                    <>
                      <Button
                        className="mb-4"
                        variant="secondary"
                        onClick={() => sendQueryEmail()}
                      >
                        Query
                      </Button>
                      <QuoteRejectDialog
                        quoteDbID={singleQuote.id}
                        quoteDisplayID={singleQuote.quote_number}
                        updateQuoteStatus={updateQuoteStatus}
                      />
                      <QuoteAcceptDialog
                        quoteDbID={singleQuote.id}
                        quoteDisplayID={singleQuote.quote_number}
                        updateQuoteStatus={updateQuoteStatus}
                      />
                    </>
                  ) : (
                    <TooltipProvider>
                      <Tooltip delayDuration={150}>
                        <TooltipTrigger>
                          <div className="text-quote-open flex h-min items-center gap-2 rounded-md bg-neutral-800/20 p-2 py-1.5 outline-[1.5px] outline-neutral-800 select-none">
                            <Clock className="h-5 w-5" />
                            Quote Open
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Your quote has been created and sent. We’re waiting
                            for your response or action.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )
                ) : singleQuote.quote_status === "expired" ? (
                  <TooltipProvider>
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger>
                        <div className="text-quote-expired flex h-min items-center gap-2 rounded-md bg-neutral-800/20 p-2 py-1.5 outline-[1.5px] outline-neutral-800 select-none">
                          <ClockAlert className="h-5 w-5" />
                          Quote Expired
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          This quote was open for more than 30 days and is now
                          no longer valid.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : singleQuote.quote_status === "draft" ? (
                  <TooltipProvider>
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger>
                        <div className="text-primary flex h-min items-center gap-2 rounded-md bg-neutral-800/20 p-2 py-1.5 outline-[1.5px] outline-neutral-800 select-none">
                          <Clock className="h-5 w-5" />
                          Quote Draft
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Your quote has been created but pricing still being
                          validated. Quote will display without values
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <div className="text-red-500">
                    Unknown Status: {singleQuote.quote_status}
                  </div>
                )}
              </div>
            </div>

            <DataTable
              columns={columns}
              data={convertedItems}
              includeSelectRow={false}
              disableColumnSelection={true}
            />
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default SingleQuoteTable;
